/**
 * @fileoverview Director-disclosure label resolution
 * @module lib/director-disclosure
 *
 * Purpose:
 * - The "director-declared value" concept surfaces under a different source
 *   document depending on the appointment type. Corporate insolvencies use a
 *   ROCAP; bankruptcies use a Statement of Affairs; a members' voluntary
 *   liquidation uses a Declaration of Solvency (Form 520); a small business
 *   restructuring uses a Restructuring Proposal Statement.
 * - This module maps an appointment type to the correct display label so the
 *   practitioner-facing UI and exports speak the right language. It is purely a
 *   labelling concern: the underlying data model is unchanged and all of these
 *   documents are treated identically on the backend.
 *
 * Used by:
 * - Asset list panel/table, Loans panel/table, the Estimated Outcome waterfall,
 *   and the CSV export builders for assets and loans.
 */

import { isPersonalInsolvencyAppointmentType } from "@cvx/lib/appointmentTypes";

/**
 * Display labels for a director-disclosure document.
 */
export interface DirectorDisclosureLabel {
  /**
   * Short token for column headers, buttons, section titles, and badges
   * (e.g. "ROCAP", "SOA", "Form 520").
   */
  short: string;
  /**
   * Full name for prose, tooltips, and derived-source notes
   * (e.g. "Statement of Affairs").
   */
  full: string;
}

const ROCAP: DirectorDisclosureLabel = { short: "ROCAP", full: "ROCAP" };
const STATEMENT_OF_AFFAIRS: DirectorDisclosureLabel = {
  short: "SOA",
  full: "Statement of Affairs",
};
const DECLARATION_OF_SOLVENCY: DirectorDisclosureLabel = {
  short: "Form 520",
  full: "Declaration of Solvency (Form 520)",
};
const RESTRUCTURING_PROPOSAL_STATEMENT: DirectorDisclosureLabel = {
  short: "RPS",
  full: "Restructuring Proposal Statement",
};

/**
 * Resolve the director-disclosure label for an appointment type.
 *
 * @description
 * Defaults to ROCAP for corporate insolvency appointments and whenever the
 * appointment type is unknown/undefined (the corporate flow is the status quo
 * and the safe default). Accepts a loose `string` so callers can pass the raw
 * `job.appointmentType` without coupling to the schema union type.
 *
 * @param appointmentType - The job's appointment type (may be undefined).
 * @returns The short/full display labels for the disclosure document.
 *
 * @example
 * directorDisclosureLabel("personal_bankruptcy").short; // "SOA"
 * directorDisclosureLabel("cvl").short;                 // "ROCAP"
 */
export function directorDisclosureLabel(
  appointmentType?: string | null,
): DirectorDisclosureLabel {
  // Every personal insolvency files a statement of affairs — a Part X
  // debtor included (MOR-1838).
  if (isPersonalInsolvencyAppointmentType(appointmentType)) {
    return STATEMENT_OF_AFFAIRS;
  }
  switch (appointmentType) {
    case "mvl":
      return DECLARATION_OF_SOLVENCY;
    case "sbr":
      return RESTRUCTURING_PROPOSAL_STATEMENT;
    default:
      return ROCAP;
  }
}
